import React, { useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import Switch from "react-switch";
import useSearchCareProviders from "../../hooks/useSearchCareProviders";
import { PERMISSIONS } from "../../utils/permissions";

// The privileges the backend actually accepts (CareProviderPrivilege), grouped
// by what they act on so each resource reads as one row of view/add/edit.
const PRIVILEGE_GROUPS = [
  {
    label: "Functional Scores",
    read: PERMISSIONS.READ_FUNCTIONAL_SCORE,
    create: PERMISSIONS.CREATE_FUNCTIONAL_SCORE,
    update: PERMISSIONS.UPDATE_FUNCTIONAL_SCORE,
  },
  {
    label: "Functional Checklists",
    read: PERMISSIONS.READ_FUNCTIONAL_CHECKLIST,
    create: PERMISSIONS.CREATE_FUNCTIONAL_CHECKLIST,
    update: PERMISSIONS.UPDATE_FUNCTIONAL_CHECKLIST,
  },
  {
    label: "Care Plans",
    read: PERMISSIONS.READ_CAREPLAN,
    create: PERMISSIONS.CREATE_CAREPLAN,
    update: PERMISSIONS.UPDATE_CAREPLAN,
  },
];

const PRIVILEGE_ACTIONS = ["read", "create", "update"];
const READ_PRIVILEGES = PRIVILEGE_GROUPS.map((group) => group.read);

const SelectContributorsModal = ({
  selectedContributors = [],
  onContributorsChange = () => {},
}) => {
  // Contributors modal state
  const [contributorSearchTerm, setContributorSearchTerm] = useState("");
  const [contributorSearchDropdownOpen, setContributorSearchDropdownOpen] =
    useState(false);
  const [currentContributor, setCurrentContributor] = useState(null);
  const [readOnlyAccess, setReadOnlyAccess] = useState(false);
  // The CareProviderPrivilege values granted to the contributor being
  // configured, exactly as the admit endpoint expects them.
  const [privileges, setPrivileges] = useState([]);

  // Fetch care providers using React Query
  const {
    data: careProvidersData,
    isLoading: careProvidersLoading,
    error: careProvidersError,
  } = useSearchCareProviders(contributorSearchTerm);

  // Get care providers from API
  const careProviders = careProvidersData?.data?.content || [];

  // Contributors modal functions
  const getFilteredContributors = () => {
    if (!contributorSearchTerm.trim() || !careProviders.length) return [];

    // API already filters by name, so return the results directly
    return careProviders.slice(0, 5);
  };

  const handleContributorSearchChange = (value) => {
    setContributorSearchTerm(value);
    setContributorSearchDropdownOpen(value.trim().length > 0);
  };

  const handleContributorSelect = (contributor) => {
    // Check if contributor is not already selected
    if (!selectedContributors.find((c) => c.id === contributor.id)) {
      setContributorSearchTerm(
        `${contributor?.firstName} ${contributor?.lastName}`,
      );
      setCurrentContributor(contributor);
      setContributorSearchDropdownOpen(false);
    }
  };

  const handleRemoveContributor = (contributorId) => {
    const updatedContributors = selectedContributors.filter(
      (c) => c.id !== contributorId,
    );
    onContributorsChange(updatedContributors);
  };

  const hasPrivilege = (privilege) => privileges.includes(privilege);

  // Creating or updating something you can't read makes no sense, so those two
  // pull in the group's read privilege, and dropping read drops all three.
  const handlePrivilegeToggle = (group, action) => {
    const privilege = group[action];

    setPrivileges((prev) => {
      if (prev.includes(privilege)) {
        return action === "read"
          ? prev.filter(
              (p) =>
                p !== group.read && p !== group.create && p !== group.update,
            )
          : prev.filter((p) => p !== privilege);
      }

      const next = [...prev, privilege];
      if (action !== "read" && !next.includes(group.read)) {
        next.push(group.read);
      }
      return next;
    });
  };

  // Read only access is the three read privileges and nothing else.
  const handleReadOnlyToggle = (checked) => {
    setReadOnlyAccess(checked);
    if (checked) {
      setPrivileges([...READ_PRIVILEGES]);
    }
  };

  const handleResetContributors = () => {
    // Reset current contributor and privileges only
    setCurrentContributor(null);
    setPrivileges([]);
    setReadOnlyAccess(false);
    setContributorSearchTerm("");
    setContributorSearchDropdownOpen(false);
  };

  const handleAddContributors = () => {
    if (currentContributor) {
      // Add current contributor with their assigned privileges
      const contributorWithPrivileges = {
        ...currentContributor,
        privileges: [...privileges],
        readOnlyAccess: readOnlyAccess,
      };

      onContributorsChange([
        ...selectedContributors,
        contributorWithPrivileges,
      ]);

      // Reset for next contributor
      setCurrentContributor(null);
      setContributorSearchTerm("");
      setPrivileges([]);
      setReadOnlyAccess(false);
    }
  };

  return (
    <div
      className="modal fade"
      tabIndex="-1"
      id="selectContributors"
      aria-hidden="true"
      aria-labelledby="selectContributors"
    >
      <div className="modal-dialog" style={{ width: "395px" }}>
        <div className="modal-content border-0 overlay-rounded">
          <div className="modal-header mt-14 border-0">
            <div className="flex-grow-1 text-center">
              <h5 className="text_primary fs-18 font-weight-bold">
                Select Contributors and Assign Privileges
              </h5>
            </div>
            <FaTimes
              size={15}
              className="cursor-pointer text_primary"
              data-dismiss="modal"
              aria-label="Close"
            />
          </div>
          <div className="modal-body px-3">
            {/* Selected Contributors List */}
            {selectedContributors.length > 0 && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="small-text font-weight-bold mb-0">
                    Selected Contributors:
                  </h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => onContributorsChange([])}
                  >
                    Clear All
                  </button>
                </div>
                {selectedContributors.map((contributor) => (
                  <div
                    key={contributor.id}
                    className="d-flex justify-content-between align-items-center mb-2 p-2 border rounded"
                  >
                    <div>
                      <small className="font-weight-bold">
                        {contributor?.firstName} {contributor?.lastName}
                      </small>
                      <br />
                      <small className="text-muted">
                        {contributor.positionName ||
                          contributor.role ||
                          "Care Provider"}
                      </small>
                      <br />
                      <small className="text-muted">
                        {contributor.privileges?.length || 0} privilege
                        {contributor.privileges?.length === 1 ? "" : "s"}
                      </small>
                    </div>
                    <FaTimes
                      size={12}
                      className="cursor-pointer text-danger"
                      onClick={() => handleRemoveContributor(contributor.id)}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Search Contributors */}
            <div className="form-group mb-4">
              <div className="dropdown">
                <div className="position-relative">
                  <FaSearch
                    size={12}
                    style={{
                      position: "absolute",
                      left: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#6c757d",
                      zIndex: 2,
                    }}
                  />
                  <input
                    type="text"
                    className="btn btn-block border text-left overlay-dropdown-input small-text"
                    placeholder="Type to search care providers..."
                    value={contributorSearchTerm}
                    onChange={(e) =>
                      handleContributorSearchChange(e.target.value)
                    }
                    style={{
                      paddingLeft: "35px",
                      paddingRight: "12px",
                    }}
                  />
                </div>

                {/* Search Results Dropdown */}
                <div
                  className={`dropdown-menu w-100 ${
                    contributorSearchDropdownOpen ? "show" : ""
                  }`}
                >
                  <p className="pl-3 mb-0 pb-2 font-weight-bold fs-13">
                    Search Results:
                  </p>
                  {careProvidersLoading ? (
                    <div className="dropdown-item small-text text-muted">
                      Loading care providers...
                    </div>
                  ) : careProvidersError ? (
                    <div className="dropdown-item small-text text-danger">
                      Error loading care providers
                    </div>
                  ) : getFilteredContributors().length > 0 ? (
                    getFilteredContributors().map((contributor) => (
                      <a
                        key={contributor.id}
                        className="dropdown-item small-text"
                        href="#"
                        onClick={() => handleContributorSelect(contributor)}
                      >
                        <div>
                          <div className="font-weight-bold">
                            {contributor?.firstName} {contributor?.lastName}
                          </div>
                          <small className="text-muted">
                            {contributor.positionName ||
                              contributor.role ||
                              "Care Provider"}
                          </small>
                        </div>
                      </a>
                    ))
                  ) : contributorSearchTerm.trim() ? (
                    <div className="dropdown-item small-text text-muted">
                      No care providers found matching "{contributorSearchTerm}"
                    </div>
                  ) : (
                    <div className="dropdown-item small-text text-muted">
                      Type to search care providers
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Privileges - Only show when a contributor is being configured */}
            {currentContributor && (
              <div className="mb-4">
                <div className="mb-3 p-2 bg-light rounded">
                  <small className="text-muted">
                    Configuring privileges for:
                  </small>
                  <div className="font-weight-bold">
                    {currentContributor?.firstName}{" "}
                    {currentContributor?.lastName}
                  </div>
                  <small className="text-muted">
                    {currentContributor.position.name ||
                      currentContributor.role ||
                      "Care Provider"}
                  </small>
                </div>

                <h6 className="small-text font-weight-bold mb-3">
                  Privileges:
                </h6>

                <div className="border rounded p-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="fs-13 text-muted">Can</span>
                    <div className="d-flex">
                      {["View", "Add", "Edit"].map((action) => (
                        <span
                          key={action}
                          className="fs-13 text-muted text-center privilege-col"
                        >
                          {action}
                        </span>
                      ))}
                    </div>
                  </div>

                  {PRIVILEGE_GROUPS.map((group) => (
                    <div
                      key={group.label}
                      className="d-flex justify-content-between align-items-center mb-3"
                    >
                      <span className="small-text">{group.label}</span>
                      <div className="d-flex">
                        {PRIVILEGE_ACTIONS.map((action) => (
                          <span
                            key={action}
                            className="privilege-col d-flex justify-content-center"
                          >
                            <Switch
                              checked={hasPrivilege(group[action])}
                              onChange={() =>
                                handlePrivilegeToggle(group, action)
                              }
                              disabled={readOnlyAccess && action !== "read"}
                              onColor="#28a745"
                              offColor="#ccc"
                              height={20}
                              width={40}
                              handleDiameter={16}
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Read Only Access - Center Justified */}
                  <div className="d-flex justify-content-center align-items-center mb-4 mt-4">
                    <span className="small-text mr-3">Read only Access</span>
                    <Switch
                      checked={readOnlyAccess}
                      onChange={handleReadOnlyToggle}
                      onColor="#28a745"
                      offColor="#ccc"
                      height={20}
                      width={40}
                      handleDiameter={16}
                    />
                  </div>

                  {/* Action Buttons - Reset and Add */}
                  <div className="d-flex justify-content-between mb-0">
                    <button
                      type="button"
                      className="btn btn_outline_danger px-4 fs-13"
                      onClick={handleResetContributors}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary px-4 fs-13"
                      onClick={handleAddContributors}
                      disabled={!currentContributor}
                    >
                      {currentContributor
                        ? `Add ${currentContributor?.firstName} ${currentContributor?.lastName}`
                        : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Cancel Button - Bottom */}
          <div className="modal-footer d-flex justify-content-between border-0">
            <button
              type="button"
              className="btn btn_outline_danger overlay-btn "
              data-dismiss="modal"
            >
              CANCEL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectContributorsModal;
